import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useApp } from '../context/AppContext';

// Los textos salen del diccionario (t); target, view, fallback y requireAction
// son identificadores y no se traducen.
const pasos = (t) => [
    {
        target: '.sidebar',
        view: 'dashboard',
        title: t.tutoStep1Title,
        content: t.tutoStep1Content
    },
    {
        target: '.topbar select',
        view: 'dashboard',
        title: t.tutoStep2Title,
        content: t.tutoStep2Content
    },
    {
        target: '.dashboard-personalizar-btn',
        fallback: '.page-content',
        view: 'dashboard',
        title: t.tutoStep3Title,
        content: t.tutoStep3Content
    },
    {
        target: '.sm-grid-wrap',
        fallback: '.page-content',
        view: 'monthly',
        modo: 'seguimiento',
        title: t.tutoStep5Title,
        content: t.tutoStep5Content
    },
    {
        // El conmutador Seguimiento / Presupuesto está en la barra superior de
        // Seguimiento Mensual; para continuar hay que pulsar "Presupuesto".
        target: '.settings-toggle',
        view: 'monthly',
        modo: 'seguimiento',
        title: t.tutoStep4Title,
        content: t.tutoStep4Content,
        requireAction: 'monthlyMode'
    },
    {
        // Ya en la pestaña Presupuesto: cómo se edita, añade y quita.
        target: '.budget-lock-btn',
        fallback: '.page-content',
        view: 'monthly',
        modo: 'presupuesto',
        title: t.tutoStepPresuTitle,
        content: t.tutoStepPresuContent
    },
    {
        target: '.networth-view-container, .page-content',
        fallback: '.page-content',
        view: 'networth',
        title: t.tutoStep6Title,
        content: t.tutoStep6Content
    },
    {
        target: '#tour-category-manager',
        fallback: '.page-content',
        view: 'settings',
        title: t.tutoStep7Title,
        content: t.tutoStep7Content
    },
    {
        target: null, // Full screen darken
        view: 'dashboard',
        title: t.tutoStep8Title,
        content: t.tutoStep8Content
    }
];

export default function TutorialWizard({ onClose, onComplete, activeView, setActiveView, monthlyMode, setMonthlyMode }) {
    const { t, tf } = useApp();
    // Memorizado por idioma: `step` va en las dependencias del efecto de abajo y
    // debe mantener su identidad entre renders.
    const STEPS = useMemo(() => pasos(t), [t]);
    const [currentStep, setCurrentStep] = useState(0);
    const [rect, setRect] = useState(null);
    const [initialMode, setInitialMode] = useState(monthlyMode);
    const [actionCompleted, setActionCompleted] = useState(false);
    // El modo del dashboard se lee por referencia: el efecto de abajo solo debe
    // ejecutarse al cambiar de paso, no cada vez que el usuario alterna el modo
    // (eso reiniciaría la acción pedida en el paso del tutorial).
    const modoActual = useRef(monthlyMode);
    useEffect(() => { modoActual.current = monthlyMode; }, [monthlyMode]);

    const step = STEPS[currentStep];

    useEffect(() => {
        setActionCompleted(false);
        setInitialMode(modoActual.current);
        // Prevent unnescessary jumps initially
        if (activeView !== step.view) {
            setActiveView(step.view);
        }
        // Pestaña de Seguimiento Mensual que pide el paso (Seguimiento o Presupuesto).
        if (step.modo && setMonthlyMode && modoActual.current !== step.modo) {
            setMonthlyMode(step.modo);
        }
    }, [currentStep, step, activeView, setActiveView, setMonthlyMode]);

    // Medición del elemento a resaltar. Va aparte del efecto anterior para
    // repetirla cuando cambia la pestaña (la barra superior se reordena al
    // ocultar el selector de mes) o el tamaño de la ventana, sin reiniciar la
    // acción pedida al usuario.
    useEffect(() => {
        let timer = null;
        const medir = () => {
            let el = null;
            if (step.target) {
                // Find elements matching target, take the first visible one
                const matches = document.querySelectorAll(step.target);
                for (let m of matches) {
                    if (m.offsetParent !== null) { el = m; break; }
                }
                if (!el && step.fallback) {
                    el = document.querySelector(step.fallback);
                }
            }

            if (el) {
                const bounds = el.getBoundingClientRect();
                if (bounds.width > 0 && bounds.height > 0) {
                    // Adjust bounds slightly for padding visually inside UI
                    setRect({
                        top: bounds.top, left: bounds.left, width: bounds.width, height: bounds.height,
                        right: bounds.right, bottom: bounds.bottom
                    });
                } else {
                    setRect(null);
                }
            } else {
                setRect(null);
            }
        };
        const programar = () => { clearTimeout(timer); timer = setTimeout(medir, 400); }; // Wait for animations and React renders
        programar();
        window.addEventListener('resize', programar);
        return () => { clearTimeout(timer); window.removeEventListener('resize', programar); };
    }, [currentStep, step, activeView, monthlyMode]);

    useEffect(() => {
        if (step.requireAction === 'monthlyMode' && monthlyMode !== initialMode) {
            setActionCompleted(true);
        }
    }, [monthlyMode, initialMode, step.requireAction]);

    const canProceed = !step.requireAction || actionCompleted;

    const handleNext = () => {
        if (currentStep === STEPS.length - 1) {
            onComplete();
            onClose();
        } else {
            setCurrentStep(s => s + 1);
        }
    };

    const handleSkip = () => {
        onComplete();
        onClose();
    };

    let dialogStyle = {
        position: 'fixed',
        zIndex: 99999,
        background: 'var(--bg-primary)',
        borderRadius: 24,
        width: '90%', maxWidth: 420,
        padding: 30,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
        border: '1px solid var(--border)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    };

    if (rect) {
        if (rect.bottom + 350 < window.innerHeight) {
            dialogStyle.top = rect.bottom + 24;
            dialogStyle.left = '50%';
            dialogStyle.transform = 'translateX(-50%)';
        }
        else if (rect.top - 350 > 0) {
            dialogStyle.bottom = (window.innerHeight - rect.top) + 24;
            dialogStyle.left = '50%';
            dialogStyle.transform = 'translateX(-50%)';
        }
        else {
            // Floating fallback explicitly center-right or center if too small
            dialogStyle.top = '50%';
            dialogStyle.left = '50%';
            dialogStyle.transform = 'translate(-50%, -50%)';
        }
    } else {
        dialogStyle.top = '50%';
        dialogStyle.left = '50%';
        dialogStyle.transform = 'translate(-50%, -50%)';
    }

    return (
        <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 99990, pointerEvents: 'none' }}>
                {rect && (
                    <>
                        {/* 4 blocker divs instead of box-shadow to be 100% sure interaction works in the hole */}
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: rect.top - 8, background: 'rgba(0, 11, 24, 0.75)', pointerEvents: 'auto' }} />
                        <div style={{ position: 'absolute', top: rect.bottom + 8, left: 0, right: 0, bottom: 0, background: 'rgba(0, 11, 24, 0.75)', pointerEvents: 'auto' }} />
                        <div style={{ position: 'absolute', top: rect.top - 8, left: 0, width: rect.left - 8, height: rect.height + 16, background: 'rgba(0, 11, 24, 0.75)', pointerEvents: 'auto' }} />
                        <div style={{ position: 'absolute', top: rect.top - 8, left: rect.right + 8, right: 0, height: rect.height + 16, background: 'rgba(0, 11, 24, 0.75)', pointerEvents: 'auto' }} />

                        {/* Glow/Border for the hole */}
                        <div style={{
                            position: 'absolute',
                            top: rect.top - 8,
                            left: rect.left - 8,
                            width: rect.width + 16,
                            height: rect.height + 16,
                            borderRadius: 16,
                            border: '2px solid var(--accent)',
                            boxShadow: '0 0 20px var(--accent-light)',
                            pointerEvents: 'none'
                        }} />
                    </>
                )}
                {!rect && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0, 11, 24, 0.75)', pointerEvents: 'auto' }} />
                )}
            </div>

            <div style={{ ...dialogStyle, pointerEvents: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--accent)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {tf('tutoStepOf', { n: currentStep + 1, total: STEPS.length })}
                    </div>
                    <button onClick={handleSkip} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        {t.tutoSkip}
                    </button>
                </div>

                <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 16px 0', color: 'var(--text-primary)', lineHeight: 1.2 }}>
                    {step.title}
                </h2>

                <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 24px 0' }}>
                    {step.content}
                </p>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 32 }}>
                    {currentStep > 0 && (
                        <button
                            onClick={() => setCurrentStep(s => s - 1)}
                            className="btn btn-ghost"
                            style={{ padding: '10px 16px', fontSize: 14 }}
                        >
                            {t.tutoBack}
                        </button>
                    )}
                    <button
                        onClick={handleNext}
                        className={`btn ${canProceed ? 'btn-primary' : 'btn-ghost'}`}
                        style={{ padding: '10px 20px', fontSize: 15, opacity: canProceed ? 1 : 0.5, cursor: canProceed ? 'pointer' : 'not-allowed' }}
                        disabled={!canProceed}
                    >
                        {currentStep === STEPS.length - 1 ? t.tutoLetsGo : (canProceed ? t.tutoNext : t.tutoInteract)}
                    </button>
                </div>
            </div>
        </>
    );
}
